import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryOptions,
} from "@tanstack/react-query";
import { ServerInferRequest } from "@ts-rest/core";
import { integrationTransactionContract } from "../common/contracts/integration-transaction.contract";
import { getBackendAPIClient } from "../lib/backendAPIClient";
import { IntegrationTransactionDto } from "../common/dto/transactions/integration-transaction.dto";
import { IntegrationTransaction } from "../common/types/integrationTransaction";

export const integrationTransactionContractClient = getBackendAPIClient(
    integrationTransactionContract,
);

export const integrationTransactionQueryKeys = {
    all: ["integration-transaction"] as const,
    byIntegrationAccountId: (integrationAccountId: string) =>
        ["integration-transaction", integrationAccountId] as const,
};

export function useIntegrationTransactionQuery(
    integrationAccountId: string,
    queryOptions: Omit<
        UseQueryOptions<IntegrationTransaction[]>,
        "queryKey" | "queryFn"
    > = {},
) {
    return useQuery({
        queryKey:
            integrationTransactionQueryKeys.byIntegrationAccountId(
                integrationAccountId,
            ),
        queryFn: async () => {
            const response =
                await integrationTransactionContractClient.getIntegrationTransactions(
                    {
                        params: {
                            integrationAccountId,
                        },
                    },
                );

            return response.body.data.map((item) =>
                IntegrationTransactionDto.deserialize(item),
            );
        },
        ...queryOptions,
    });
}

export type IntegrationTransactionUpdateBody = ServerInferRequest<
    typeof integrationTransactionContract.updateIntegrationTransaction
>["body"];

export function useIntegrationTransactionUpdateMutation(
    transaction: IntegrationTransaction,
) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (body: IntegrationTransactionUpdateBody) => {
            const response =
                await integrationTransactionContractClient.updateIntegrationTransaction(
                    {
                        params: {
                            integrationAccountId:
                                transaction.integrationAccountId,
                            integrationTransactionId: transaction.id,
                        },
                        body,
                    },
                );

            return IntegrationTransactionDto.deserialize(response.body);
        },
        onSuccess: (updatedTransaction) => {
            queryClient.setQueryData(
                integrationTransactionQueryKeys.byIntegrationAccountId(
                    transaction.integrationAccountId,
                ),
                (transactions: IntegrationTransaction[]) =>
                    transactions.map((t) =>
                        t.id === updatedTransaction.id ? updatedTransaction : t,
                    ),
            );
        },
    });
}
